import {
  MRT_TableContainer as MaterialTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import { OnChangeFn, VisibilityState } from "@tanstack/table-core";
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ClipboardEvent,
} from "react";
import {
  AverageDistanceTravelled,
  AverageManufacturingEur,
  CategoryDlzShare,
  CategoryMix,
  CategoryPackMix,
  CountryAverageDistanceTravelled,
  CountryAverageManufacturingEur,
  CountryPackagingAverageRecoveryRate,
  CountrySalesGrowth,
  EntityWeight,
  EntityWeightAggregate,
  IngredientWeightEntity,
  LogisticsDistanceEntityWithCountryDto,
  ManufacturingEurEntityWithCountryDto,
  PackagingAverageNetLossRate,
  PackagingAverageRecoveryRate,
  PackagingAverageRecycledContent,
  PackagingNetLossRateEntityDto,
  PackagingRecoveryRateEntityWithCountryDto,
  PackagingRecycledContentEntityDto,
  PackagingWeightEntityDto,
  PackMix,
  PackTypeDlzShare,
  SalesMixCategoryMixEntityDto,
  SalesMixDlzShareEntityDto,
  SalesMixPackMixEntityDto,
  SalesMixSalesGrowthEntityDto,
  WeightedAverageNetLossRateAggregate,
  WeightedAverageRecycledContentAggregate,
  YearCategoryMix,
} from "orval/generated/models";
import {
  formatNumberOrEmpty,
  getDecimalPlacesForDataStructureKey,
} from "utils/formatNumber/formatNumber";
import { AppConstant } from "constants/AppConstant";
import {
  BasicTableAgreggatedDataType,
  MacroAdjustmentCombinations,
  MacroAdjustmentWizardFormikValues,
} from "../MacroAdjustmentTypes";
import { Box, Typography, useTheme } from "@mui/material";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { ProductConfigurationsMacro } from "features/micro-tool/components/MicroAdjustmentsTable/types";
import {
  useGetCategoriesCategoriesGet,
  useGetPackTypesPackTypesGet,
} from "orval/generated/endpoint";
import {
  convertIsocodeToCountry,
  useCountries,
} from "utils/CountriesProvider/CountriesProvider";
import { deepClone } from "utils";

export type TableEntityType = {
  id?: number;
  baseYear?: number;
  targetYear?: number;
  adjusted_value: number | null;
  action?: "percentage" | "new_value" | null;
  country?: string;
  category?: number;
  pack_type?: number;
  salesGrowth?: number;
};

export type MacroAdjustmentStepThreeBasicTableProps = {
  validationErrors: { [key: string]: string | undefined };
  setValidationErrors: (
    errors: (prev: { [key: string]: string | undefined }) => {
      [key: string]: string | undefined;
    },
  ) => void;
  base_year: number;
  target_year: number;
  adjustmentCombination: MacroAdjustmentCombinations | undefined;
  aggregatedData:
    | BasicTableAgreggatedDataType
    | CountryPackagingAverageRecoveryRate
    | CountryAverageManufacturingEur
    | CountryAverageDistanceTravelled;
};

function MacroAdjustmentStepThreeBasicTable({
  validationErrors,
  setValidationErrors,
  base_year,
  target_year,
  adjustmentCombination,
  aggregatedData,
}: MacroAdjustmentStepThreeBasicTableProps) {
  const { palette } = useTheme();
  const { countries } = useCountries();

  const { t } = useTranslation(["macro", "common"]);
  const { data: categories } = useGetCategoriesCategoriesGet({
    query: {
      enabled: [MacroAdjustmentCombinations.SalesMix_CategoryMix].includes(
        adjustmentCombination as MacroAdjustmentCombinations,
      ),
    },
  });

  const { data: packTypes } = useGetPackTypesPackTypesGet({
    query: {
      enabled: [
        MacroAdjustmentCombinations.SalesMix_PackMix,
        MacroAdjustmentCombinations.SalesMix_DLZShare,
      ].includes(adjustmentCombination as MacroAdjustmentCombinations),
    },
  });

  const { setFieldValue, values } =
    useFormikContext<MacroAdjustmentWizardFormikValues>();

  const [columnVisibility, setColumnVisibility] = useState({
    baseYear: false,
    targetYear: false,
    salesGrowth: false,
    country: false,
    category: false,
    pack_type: false,
    id: false,
  });
  const [enableKeyboardShortcuts, setEnableKeyboardShortcuts] =
    useState<boolean>(true);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, row: any) => {
      const updatedEntity = { ...row.original };

      const newValue = e.target.value === "" ? null : Number(e.target.value);
      switch (adjustmentCombination) {
        case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
        case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment: {
          const payloadIngredientEntities: IngredientWeightEntity[] = values
            .payload.entities as IngredientWeightEntity[];
          setFieldValue(
            "payload.entities",
            payloadIngredientEntities.map((entity: IngredientWeightEntity) => {
              if (entity.ingredient_id === updatedEntity.id) {
                return {
                  ...entity,
                  ingredient_id: updatedEntity.id,
                  weight: newValue,
                } as IngredientWeightEntity;
              }
              return entity;
            }),
          ).catch((e) => console.warn(e));
          break;
        }
        case MacroAdjustmentCombinations.Packaging_RecoveryRate: {
          const payloadPackagingEntities: PackagingRecoveryRateEntityWithCountryDto[] =
            values.payload
              .entities as PackagingRecoveryRateEntityWithCountryDto[];
          const updatedEntities = payloadPackagingEntities.map(
            (entity: PackagingRecoveryRateEntityWithCountryDto) => {
              if (
                entity.part_material_id === updatedEntity.id &&
                entity.country === updatedEntity.country
              ) {
                return {
                  ...entity,
                  part_material_id: updatedEntity.id,
                  recovery_rate: newValue,
                } as PackagingRecoveryRateEntityWithCountryDto;
              }
              return entity;
            },
          );

          if (
            !updatedEntities.find(
              (e) =>
                e.part_material_id === updatedEntity.id &&
                e.country === updatedEntity.country,
            )
          )
            updatedEntities.push({
              part_material_id: updatedEntity.id,
              recovery_rate: newValue as number,
              country: updatedEntity.country,
            });

          setFieldValue("payload.entities", updatedEntities).catch((e) =>
            console.warn(e),
          );
          break;
        }
        case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur: {
          const payloadEntities: ManufacturingEurEntityWithCountryDto[] = values
            .payload.entities as ManufacturingEurEntityWithCountryDto[];
          const updatedEntities = payloadEntities.map(
            (entity: ManufacturingEurEntityWithCountryDto) => {
              if (
                entity.energy_type_id === updatedEntity.id &&
                entity.country === updatedEntity.country
              ) {
                return {
                  ...entity,
                  energy_type_id: updatedEntity.id,
                  eur: newValue,
                } as ManufacturingEurEntityWithCountryDto;
              }
              return entity;
            },
          );

          if (
            !updatedEntities.find(
              (e) =>
                e.energy_type_id === updatedEntity.id &&
                e.country === updatedEntity.country,
            )
          )
            updatedEntities.push({
              energy_type_id: updatedEntity.id,
              eur: newValue as number,
              country: updatedEntity.country,
            });

          setFieldValue("payload.entities", updatedEntities).catch((e) =>
            console.warn(e),
          );
          break;
        }
        case MacroAdjustmentCombinations.Logistics_DistanceTravelled: {
          const payloadEntities: LogisticsDistanceEntityWithCountryDto[] =
            values.payload.entities as LogisticsDistanceEntityWithCountryDto[];
          const updatedEntities = payloadEntities.map(
            (entity: LogisticsDistanceEntityWithCountryDto) => {
              if (
                entity.transport_mode_id === updatedEntity.id &&
                entity.country === updatedEntity.country
              ) {
                return {
                  ...entity,
                  transport_mode_id: updatedEntity.id,
                  distance: newValue,
                } as LogisticsDistanceEntityWithCountryDto;
              }
              return entity;
            },
          );

          if (
            !updatedEntities.find(
              (e) =>
                e.transport_mode_id === updatedEntity.id &&
                e.country === updatedEntity.country,
            )
          )
            updatedEntities.push({
              transport_mode_id: updatedEntity.id,
              distance: newValue as number,
              country: updatedEntity.country,
            });

          setFieldValue("payload.entities", updatedEntities).catch((e) =>
            console.warn(e),
          );
          break;
        }
        case MacroAdjustmentCombinations.Packaging_RecycledContent: {
          const payloadEntities: PackagingRecycledContentEntityDto[] = values
            .payload.entities as PackagingRecycledContentEntityDto[];
          setFieldValue(
            "payload.entities",
            payloadEntities.map((entity: PackagingRecycledContentEntityDto) => {
              if (entity.part_material_id === updatedEntity.id) {
                return {
                  ...entity,
                  part_material_id: updatedEntity.id,
                  recycled_content: newValue,
                } as PackagingRecycledContentEntityDto;
              }
              return entity;
            }),
          ).catch((e) => console.warn(e));
          break;
        }
        case MacroAdjustmentCombinations.Packaging_NetLossRate: {
          const payloadEntities: PackagingNetLossRateEntityDto[] = values
            .payload.entities as PackagingNetLossRateEntityDto[];
          setFieldValue(
            "payload.entities",
            payloadEntities.map((entity: PackagingNetLossRateEntityDto) => {
              if (entity.part_material_id === updatedEntity.id) {
                return {
                  ...entity,
                  part_material_id: updatedEntity.id,
                  net_loss_rate: newValue,
                } as PackagingNetLossRateEntityDto;
              }
              return entity;
            }),
          ).catch((e) => console.warn(e));
          break;
        }
        case MacroAdjustmentCombinations.SalesMix_SalesGrowth: {
          const payloadSalesGrowthEntities: SalesMixSalesGrowthEntityDto[] =
            values.payload.entities as SalesMixSalesGrowthEntityDto[];
          setFieldValue(
            "payload.entities",
            payloadSalesGrowthEntities.map(
              (entity: SalesMixSalesGrowthEntityDto) => {
                if (entity.country === updatedEntity.country) {
                  return {
                    ...entity,
                    sales_growth: newValue,
                  } as SalesMixSalesGrowthEntityDto;
                }
                return entity;
              },
            ),
          ).catch((e) => console.warn(e));
          break;
        }
        case MacroAdjustmentCombinations.SalesMix_CategoryMix: {
          const payloadCategoryMixEntities: SalesMixCategoryMixEntityDto[] =
            values.payload.entities as SalesMixCategoryMixEntityDto[];
          setFieldValue(
            "payload.entities",
            payloadCategoryMixEntities.map(
              (entity: SalesMixCategoryMixEntityDto) => {
                if (entity.category_id === updatedEntity.category) {
                  return {
                    ...entity,
                    category_mix: newValue,
                  } as SalesMixCategoryMixEntityDto;
                }
                return entity;
              },
            ),
          ).catch((e) => console.warn(e));
          break;
        }
        case MacroAdjustmentCombinations.SalesMix_PackMix: {
          const payloadPackMixEntities: SalesMixPackMixEntityDto[] = values
            .payload.entities as SalesMixPackMixEntityDto[];
          setFieldValue(
            "payload.entities",
            payloadPackMixEntities.map((entity: SalesMixPackMixEntityDto) => {
              if (
                entity.category_id === updatedEntity.category &&
                entity.pack_type_id === updatedEntity.pack_type
              ) {
                return {
                  ...entity,
                  pack_mix: newValue,
                } as SalesMixPackMixEntityDto;
              }
              return entity;
            }),
          ).catch((e) => console.warn(e));
          break;
        }
        case MacroAdjustmentCombinations.SalesMix_DLZShare: {
          const payloadDlzEntities: SalesMixDlzShareEntityDto[] = values.payload
            .entities as SalesMixDlzShareEntityDto[];
          setFieldValue(
            "payload.entities",
            payloadDlzEntities.map((entity: SalesMixDlzShareEntityDto) => {
              if (
                entity.category_id === updatedEntity.category &&
                entity.pack_type_id === updatedEntity.pack_type
              ) {
                return {
                  ...entity,
                  dlz_share: newValue,
                } as SalesMixDlzShareEntityDto;
              }
              return entity;
            }),
          ).catch((e) => console.warn(e));
          break;
        }

        default: {
          const payloadPackagingEntities: PackagingWeightEntityDto[] = values
            .payload.entities as PackagingWeightEntityDto[];
          const updatedEntities = payloadPackagingEntities.map(
            (entity: PackagingWeightEntityDto) => {
              if (entity.part_material_id === updatedEntity.id) {
                return {
                  ...entity,
                  part_material_id: updatedEntity.id,
                  weight: newValue,
                } as PackagingWeightEntityDto;
              }
              return entity;
            },
          );
          setFieldValue("payload.entities", updatedEntities).catch((e) =>
            console.warn(e),
          );
          break;
        }
      }
    },
    [adjustmentCombination, setFieldValue, values.payload.entities],
  );

  const entityColumnHeader = useMemo(() => {
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
        return t("adjustmentsPage.wizard.stepThree.ingredient");
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_RecycledContent:
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
      case MacroAdjustmentCombinations.Packaging_NetLossRate:
        return t("adjustmentsPage.wizard.stepThree.packagingMaterial");
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        return t("adjustmentsPage.wizard.stepThree.energyType");
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        return t("adjustmentsPage.wizard.stepThree.transportMode");
      default:
        return "";
    }
  }, [adjustmentCombination, t]);

  const baseYearColumnHeader = useMemo(() => {
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.ingredientsBaseYear")}`;
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.packagingBaseYear")}`;
      case MacroAdjustmentCombinations.Packaging_RecycledContent:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.recycledContent")} (%)`;
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.recoveryRate")} (%)`;
      case MacroAdjustmentCombinations.Packaging_NetLossRate:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.netLossRate")} (%)`;
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.energyUsageRatio")}`;
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.distanceTravelled")}`;
      case MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors:
        return `${base_year}`;
      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.categoryMix")} (%)`;
      case MacroAdjustmentCombinations.SalesMix_PackMix:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.packMix")} (%)`;
      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        return `${base_year} ${t("adjustmentsPage.wizard.stepThree.dlzShare")} (%)`;

      default:
        return "";
    }
  }, [adjustmentCombination, base_year, t]);

  const targetYearColumnHeader = useMemo(() => {
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.ingredientsTargetYear")}`;
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.packagingTargetYear")}`;
      case MacroAdjustmentCombinations.Packaging_RecycledContent:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.recycledContent")} (%)`;
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.recoveryRate")} (%)`;
      case MacroAdjustmentCombinations.Packaging_NetLossRate:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.netLossRate")} (%)`;
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.energyUsageRatio")}`;
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.distanceTravelled")}`;
      case MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors:
        return `${target_year}`;
      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.categoryMix")} (%)`;
      case MacroAdjustmentCombinations.SalesMix_PackMix:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.packMix")} (%)`;
      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        return `${target_year} ${t("adjustmentsPage.wizard.stepThree.dlzShare")} (%)`;

      default:
        return "";
    }
  }, [adjustmentCombination, target_year, t]);

  const adjustedValueColumnHeader = useMemo(() => {
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
        return values.selectedAction === "percentage"
          ? t(
              "adjustmentsPage.wizard.stepThree.adjustedValueHeaderPercentagePackaging",
            )
          : t(
              "adjustmentsPage.wizard.stepThree.adjustedValueHeaderNewValuePackaging",
            );
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
        return t(
          "adjustmentsPage.wizard.stepThree.adjustedValueHeaderPercentagePackaging",
        );
      case MacroAdjustmentCombinations.Packaging_RecycledContent:
        return `${t("adjustmentsPage.wizard.stepThree.override")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.recycledContent")} (%)`;
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
        return `${t("adjustmentsPage.wizard.stepThree.override")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.recoveryRate")} (%)`;
      case MacroAdjustmentCombinations.Packaging_NetLossRate:
        return `${t("adjustmentsPage.wizard.stepThree.override")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.netLossRate")} (%)`;
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        return `${t("adjustmentsPage.wizard.stepThree.adjust")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.energyUsageRatio")}`;
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
        return `${t("adjustmentsPage.wizard.stepThree.adjust")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.ingredientWeight")}`;
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        return `${t("adjustmentsPage.wizard.stepThree.adjust")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.distanceTravelled")}`;
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography variant={"body2"}>
              {t(
                "adjustmentsPage.wizard.stepThree.adjustedValueHeaderPercentageIngredients",
              )}
            </Typography>
            <Typography sx={{ color: "#6A6A6A" }} variant={"body2"}>
              ({t("adjustmentsPage.wizard.stepThree.vs")} {target_year}{" "}
              {t("adjustmentsPage.wizard.stepThree.glidepath")})
            </Typography>
          </Box>
        );
      case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{ color: palette.primary.main, fontWeight: 600 }}
              variant={"body2"}
            >
              {t(
                "adjustmentsPage.wizard.stepThree.adjustedValueHeaderOverrideSalesGrowth",
              )}
            </Typography>
            <Typography sx={{ color: "#6A6A6A" }} variant={"body2"}>
              {`${base_year} - ${target_year} ${t("adjustmentsPage.wizard.stepThree.cagr")}`}
            </Typography>
          </Box>
        );
      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
        return `${t("adjustmentsPage.wizard.stepThree.override")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.categoryMix")} (%)`;
      case MacroAdjustmentCombinations.SalesMix_PackMix:
        return `${t("adjustmentsPage.wizard.stepThree.override")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.packMix")} (%)`;
      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        return `${t("adjustmentsPage.wizard.stepThree.override")} ${target_year} ${t("adjustmentsPage.wizard.stepThree.dlzShare")} (%)`;

      default:
        return t("adjustmentsPage.wizard.stepThree.adjustedValue");
    }
  }, [
    adjustmentCombination,
    values.selectedAction,
    t,
    target_year,
    palette.primary.main,
    base_year,
  ]);

  const tableData = useMemo(() => {
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
        return values.selectedEntities.map((entity) => {
          const existingEntity = (
            values.payload
              .entities as PackagingRecoveryRateEntityWithCountryDto[]
          ).find(
            (adjustedEntity) =>
              adjustedEntity.part_material_id === entity.id &&
              adjustedEntity.country ===
                (aggregatedData as CountryPackagingAverageRecoveryRate).country,
          ) as PackagingRecoveryRateEntityWithCountryDto | undefined;

          return {
            id: entity.id,
            adjusted_value: existingEntity?.recovery_rate ?? null,
            action: "new_value",
            baseYear: (
              aggregatedData as CountryPackagingAverageRecoveryRate
            ).base_year.find(
              (item: PackagingAverageRecoveryRate) => item.id === entity.id,
            )?.avg_recovery_rate,
            targetYear: (
              aggregatedData as CountryPackagingAverageRecoveryRate
            ).target_year.find(
              (item: PackagingAverageRecoveryRate) => item.id === entity.id,
            )?.avg_recovery_rate,
            country: (aggregatedData as CountryPackagingAverageRecoveryRate)
              .country,
          };
        });
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        return values.selectedEntities.map((entity) => {
          const existingEntity = (
            values.payload.entities as ManufacturingEurEntityWithCountryDto[]
          ).find(
            (adjustedEntity) =>
              adjustedEntity.energy_type_id === entity.id &&
              adjustedEntity.country ===
                (aggregatedData as CountryAverageManufacturingEur).country,
          );

          return {
            id: entity.id,
            adjusted_value: existingEntity?.eur ?? null,
            baseYear: (
              aggregatedData as CountryAverageManufacturingEur
            ).base_year.find(
              (item: AverageManufacturingEur) => item.id === entity.id,
            )?.avg_eur,
            targetYear: (
              aggregatedData as CountryAverageManufacturingEur
            ).target_year.find(
              (item: AverageManufacturingEur) => item.id === entity.id,
            )?.avg_eur,
            country: (aggregatedData as CountryAverageManufacturingEur).country,
          };
        });
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        return values.selectedEntities.map((entity) => {
          const existingEntity = (
            values.payload.entities as LogisticsDistanceEntityWithCountryDto[]
          ).find(
            (adjustedEntity) =>
              adjustedEntity.transport_mode_id === entity.id &&
              adjustedEntity.country ===
                (aggregatedData as CountryAverageDistanceTravelled).country,
          );

          return {
            id: entity.id,
            adjusted_value: existingEntity?.distance ?? null,
            baseYear: (
              aggregatedData as CountryAverageDistanceTravelled
            ).base_year.find(
              (item: AverageDistanceTravelled) => item.id === entity.id,
            )?.avg_distance,
            targetYear: (
              aggregatedData as CountryAverageDistanceTravelled
            ).target_year.find(
              (item: AverageDistanceTravelled) => item.id === entity.id,
            )?.avg_distance,
            country: (aggregatedData as CountryAverageDistanceTravelled)
              .country,
          };
        });
      case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
        return (values.payload.entities as SalesMixSalesGrowthEntityDto[]).map(
          (entity) => {
            return {
              adjusted_value: entity.sales_growth,
              salesGrowth: (aggregatedData as CountrySalesGrowth[]).find(
                (country) => country.country === entity.country,
              )?.sales_growth,
              country: entity.country,
            };
          },
        );
      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
        return (values.payload.entities as SalesMixCategoryMixEntityDto[]).map(
          (entity) => {
            return {
              category: entity.category_id,
              adjusted_value: entity.category_mix,
              baseYear: (aggregatedData as YearCategoryMix).base_year.find(
                (category: CategoryMix) =>
                  category.category_id === entity.category_id,
              )?.category_mix,
              targetYear: (aggregatedData as YearCategoryMix).target_year.find(
                (category: CategoryMix) =>
                  category.category_id === entity.category_id,
              )?.category_mix,
              country: entity.country,
            };
          },
        );
      case MacroAdjustmentCombinations.SalesMix_PackMix:
        return (values.payload.entities as SalesMixPackMixEntityDto[])
          .filter(
            (entity) =>
              entity.category_id ===
              (aggregatedData as CategoryPackMix).category_id,
          )
          .map((entity) => {
            return {
              category: entity.category_id,
              pack_type: entity.pack_type_id,
              adjusted_value: entity.pack_mix,
              baseYear: (aggregatedData as CategoryPackMix).base_year.find(
                (packType: PackMix) =>
                  packType.pack_type_id === entity.pack_type_id,
              )?.pack_mix,
              targetYear: (aggregatedData as CategoryPackMix).target_year.find(
                (packType: PackMix) =>
                  packType.pack_type_id === entity.pack_type_id,
              )?.pack_mix,
              country: entity.country,
            };
          });
      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        return (values.payload.entities as SalesMixDlzShareEntityDto[])
          .filter(
            (entity) =>
              entity.category_id ===
              (aggregatedData as CategoryDlzShare).category_id,
          )
          .map((entity) => {
            return {
              category: entity.category_id,
              pack_type: entity.pack_type_id,
              adjusted_value: entity.dlz_share,
              baseYear: (aggregatedData as CategoryDlzShare).base_year.find(
                (packType: PackTypeDlzShare) =>
                  packType.pack_type_id === entity.pack_type_id,
              )?.dlz_share,
              targetYear: (aggregatedData as CategoryDlzShare).target_year.find(
                (packType: PackTypeDlzShare) =>
                  packType.pack_type_id === entity.pack_type_id,
              )?.dlz_share,
              country: entity.country,
            };
          });
      default:
        return values.selectedEntities.map((entity) => {
          let adjustedValue: number | undefined = undefined;
          switch (adjustmentCombination) {
            case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
            case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
              adjustedValue = (
                values.payload.entities as IngredientWeightEntity[]
              ).find(
                (adjustedEntity) => adjustedEntity.ingredient_id === entity.id,
              )?.weight;
              break;
            case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
            case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
              adjustedValue = (
                values.payload.entities as PackagingWeightEntityDto[]
              ).find(
                (adjustedEntity) =>
                  adjustedEntity.part_material_id === entity.id,
              )?.weight;
              break;
            case MacroAdjustmentCombinations.Packaging_NetLossRate:
              adjustedValue = (
                values.payload.entities as PackagingNetLossRateEntityDto[]
              ).find(
                (adjustedEntity) =>
                  adjustedEntity.part_material_id === entity.id,
              )?.net_loss_rate;
              break;
            case MacroAdjustmentCombinations.Packaging_RecycledContent:
              adjustedValue = (
                values.payload.entities as PackagingRecycledContentEntityDto[]
              ).find(
                (adjustedEntity) =>
                  adjustedEntity.part_material_id === entity.id,
              )?.recycled_content;
              break;

            default:
              break;
          }

          let tableRow: TableEntityType;
          tableRow = {
            id: entity.id,
            adjusted_value: adjustedValue ?? null,
            baseYear: (() => {
              switch (adjustmentCombination) {
                case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
                  return (
                    aggregatedData as EntityWeightAggregate
                  )?.base_year.find(
                    (item: EntityWeight) => item.id === entity.id,
                  )?.weight;
                case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
                  return (
                    aggregatedData as EntityWeightAggregate
                  )?.base_year.find(
                    (item: EntityWeight) => item.id === entity.id,
                  )?.weight;
                case MacroAdjustmentCombinations.Packaging_RecycledContent:
                  return (
                    aggregatedData as WeightedAverageRecycledContentAggregate
                  )?.base_year.find(
                    (item: PackagingAverageRecycledContent) =>
                      item.id === entity.id,
                  )?.avg_recycled_content;
                case MacroAdjustmentCombinations.Packaging_NetLossRate:
                  return (
                    aggregatedData as WeightedAverageNetLossRateAggregate
                  )?.base_year.find(
                    (item: PackagingAverageNetLossRate) =>
                      item.id === entity.id,
                  )?.avg_net_loss_rate;

                default:
                  return undefined;
              }
            })(),
            targetYear: (() => {
              switch (adjustmentCombination) {
                case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
                  return (
                    aggregatedData as EntityWeightAggregate
                  )?.target_year.find(
                    (item: EntityWeight) => item.id === entity.id,
                  )?.weight;
                case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
                  return (
                    aggregatedData as EntityWeightAggregate
                  )?.target_year.find(
                    (item: EntityWeight) => item.id === entity.id,
                  )?.weight;
                case MacroAdjustmentCombinations.Packaging_RecycledContent:
                  return (
                    aggregatedData as WeightedAverageRecycledContentAggregate
                  )?.target_year.find(
                    (item: PackagingAverageRecycledContent) =>
                      item.id === entity.id,
                  )?.avg_recycled_content;
                case MacroAdjustmentCombinations.Packaging_NetLossRate:
                  return (
                    aggregatedData as WeightedAverageNetLossRateAggregate
                  )?.target_year.find(
                    (item: PackagingAverageNetLossRate) =>
                      item.id === entity.id,
                  )?.avg_net_loss_rate;
                default:
                  return undefined;
              }
            })(),
          };
          return tableRow;
        });
    }
  }, [
    adjustmentCombination,
    aggregatedData,
    values.payload.entities,
    values.selectedEntities,
  ]);

  const getRegex = (decimalPlaces: number) => {
    return new RegExp(`^-?\\d*\\.?\\d{0,${decimalPlaces}}$`);
  };

  const isAdjustmentAllowed = useCallback(
    (id: number): boolean => {
      switch (adjustmentCombination) {
        case MacroAdjustmentCombinations.SalesMix_CategoryMix: {
          const category = (aggregatedData as YearCategoryMix).target_year.find(
            (c) => c.category_id === id,
          );

          if (category) {
            if (category.category_mix) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        }
        case MacroAdjustmentCombinations.SalesMix_PackMix: {
          const packType = (aggregatedData as CategoryPackMix).target_year.find(
            (c) => c.pack_type_id === id,
          );

          if (packType) {
            if (packType.pack_mix) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        }
        case MacroAdjustmentCombinations.SalesMix_DLZShare: {
          const packType = (
            aggregatedData as CategoryDlzShare
          ).target_year.find((c) => c.pack_type_id === id);

          if (packType) {
            if (packType.dlz_share && packType.dlz_share !== 100) {
              return false;
            } else {
              return true;
            }
          } else {
            return false;
          }
        }
        default:
          return false;
      }
    },
    [adjustmentCombination, aggregatedData],
  );

  const columns = useMemo<MRT_ColumnDef<TableEntityType>[]>(
    () => [
      {
        accessorKey: "id",
        header: entityColumnHeader,
        enableEditing: false,
        Cell: ({ row }: { row: any }) => {
          return values.selectedEntities.find(
            (entity: any) => entity.id === row.original.id,
          )?.value;
        },
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: 400,
          },
        }),
      },
      {
        accessorKey: "baseYear",
        header: baseYearColumnHeader,
        enableEditing: false,
        Cell: ({ row }) => {
          const baseYear = row.original.baseYear;
          if (baseYear == null) {
            return AppConstant.emptyCell;
          }
          return formatNumberOrEmpty(baseYear, adjustmentCombination as string);
        },
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: 400,
          },
        }),
      },
      {
        accessorKey: "targetYear",
        header: targetYearColumnHeader,
        enableEditing: false,
        Cell: ({ row }) => {
          const targetYear = row.original.targetYear;
          if (targetYear == null) {
            return AppConstant.emptyCell;
          }
          return formatNumberOrEmpty(
            targetYear,
            adjustmentCombination as string,
          );
        },
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: 400,
          },
        }),
      },
      {
        accessorKey: "country",
        header: t("adjustmentsPage.wizard.stepThree.countryColumnHeader"),
        enableEditing: false,
        Cell: ({ row }) => {
          const country = row.original.country;

          return convertIsocodeToCountry(countries, country as string);
        },
      },
      {
        accessorKey: "category",
        header: t("adjustmentsPage.wizard.stepThree.categoryColumnHeader"),
        enableEditing: false,
        Cell: ({ row }) => {
          const category = categories?.find(
            (category) => category.id === Number(row.original.category),
          )?.category;

          return category;
        },
      },
      {
        accessorKey: "pack_type",
        header: t("adjustmentsPage.wizard.stepThree.packTypeColumnHeader"),
        enableEditing: false,
        Cell: ({ row }) => {
          const packType = packTypes?.find(
            (packType) => packType.id === Number(row.original.pack_type),
          )?.pack_type;

          return packType;
        },
      },
      {
        accessorKey: "salesGrowth",
        header: t("adjustmentsPage.wizard.stepThree.salesGrowthColumnHeader"),
        enableEditing: false,
        Cell: ({ row }) => {
          const salesGrowth = row.original.salesGrowth;

          return `${formatNumberOrEmpty(
            salesGrowth,
            ProductConfigurationsMacro.TWO_DP,
          )}%`;
        },
      },
      {
        accessorKey: "adjusted_value",
        Header: adjustedValueColumnHeader,
        header: t(
          "adjustmentsPage.wizard.stepThree.adjustedValueHeaderPercentagePackaging",
        ),
        enableColumnActions: false,
        muiEditTextFieldProps: ({ row }: { row: any }) => ({
          disabled: isAdjustmentAllowed(
            adjustmentCombination ===
              MacroAdjustmentCombinations.SalesMix_CategoryMix
              ? row.original.category
              : row.original.pack_type,
          ),
          type: "number",
          required: true,
          placeholder: isAdjustmentAllowed(
            adjustmentCombination ===
              MacroAdjustmentCombinations.SalesMix_CategoryMix
              ? row.original.category
              : row.original.pack_type,
          )
            ? t("macro:adjustmentsPage.wizard.stepThree.adjustmentNotAllowed")
            : AppConstant.emptyCell,
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            handleOnChange(e, row);
          },
          error:
            !!validationErrors?.[
              `${adjustmentCombination === MacroAdjustmentCombinations.SalesMix_SalesGrowth ? row.original.country : adjustmentCombination === MacroAdjustmentCombinations.SalesMix_CategoryMix ? row.original.category : [MacroAdjustmentCombinations.SalesMix_PackMix, MacroAdjustmentCombinations.SalesMix_DLZShare].includes(adjustmentCombination as MacroAdjustmentCombinations) ? "packType-" + row.original.pack_type + "category-" + row.original.category : row.original.id}${
                [
                  MacroAdjustmentCombinations.Packaging_RecoveryRate,
                  MacroAdjustmentCombinations.Manufacturing_ManufacturingEur,
                  MacroAdjustmentCombinations.Logistics_DistanceTravelled,
                ].includes(adjustmentCombination as MacroAdjustmentCombinations)
                  ? row.original.country
                  : ""
              }` as string
            ],
          helperText:
            validationErrors?.[
              `${adjustmentCombination === MacroAdjustmentCombinations.SalesMix_SalesGrowth ? row.original.country : adjustmentCombination === MacroAdjustmentCombinations.SalesMix_CategoryMix ? row.original.category : [MacroAdjustmentCombinations.SalesMix_PackMix, MacroAdjustmentCombinations.SalesMix_DLZShare].includes(adjustmentCombination as MacroAdjustmentCombinations) ? "packType-" + row.original.pack_type + "category-" + row.original.category : row.original.id}${
                [
                  MacroAdjustmentCombinations.Packaging_RecoveryRate,
                  MacroAdjustmentCombinations.Manufacturing_ManufacturingEur,
                  MacroAdjustmentCombinations.Logistics_DistanceTravelled,
                ].includes(adjustmentCombination as MacroAdjustmentCombinations)
                  ? row.original.country
                  : ""
              }` as string
            ],
          onBlur: (event: FocusEvent<HTMLInputElement>) => {
            setEnableKeyboardShortcuts(true);
            let validationError: string | undefined = undefined;
            const value = Number(event.currentTarget.value) || 0;

            if (
              [
                MacroAdjustmentCombinations.SalesMix_SalesGrowth,
                MacroAdjustmentCombinations.SalesMix_CategoryMix,
                MacroAdjustmentCombinations.SalesMix_PackMix,
                MacroAdjustmentCombinations.SalesMix_DLZShare,
              ].includes(adjustmentCombination as MacroAdjustmentCombinations)
            ) {
              switch (adjustmentCombination) {
                case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
                  if (value < -100) {
                    validationError = t(
                      "common:validation.greaterThanOrEqualToMinusOneHundred",
                    );
                  } else {
                    validationError = undefined;
                  }
                  break;
                case MacroAdjustmentCombinations.SalesMix_CategoryMix:
                  const sumOfCategoryMixValues = (
                    values.payload.entities as SalesMixCategoryMixEntityDto[]
                  )
                    .filter((entity) => entity.category_mix !== undefined)
                    .map((entity) => entity.category_mix)
                    .reduce(
                      (accumulator, currentValue) => accumulator + currentValue,
                      0,
                    )
                    .toFixed(2);

                  if (Number(sumOfCategoryMixValues) !== 100) {
                    validationError = t(
                      "common:validation.categoryMixMustTotalOneHundred",
                    );
                  } else {
                    validationError = undefined;
                    setValidationErrors((_prev) => {
                      const newValidationErrors = deepClone(_prev);
                      Object.keys(newValidationErrors).forEach(
                        (key: string) => {
                          newValidationErrors[key] = undefined;
                        },
                      );

                      return newValidationErrors;
                    });
                  }
                  break;
                case MacroAdjustmentCombinations.SalesMix_PackMix:
                  const sumOfCategoryAccordionValues = (
                    values.payload.entities as SalesMixPackMixEntityDto[]
                  )
                    .filter(
                      (entity) =>
                        entity.category_id === row.original.category &&
                        entity.pack_mix !== undefined,
                    )
                    .map((entity) => entity.pack_mix)
                    .reduce(
                      (accumulator, currentValue) => accumulator + currentValue,
                      0,
                    )
                    .toFixed(2);

                  if (Number(sumOfCategoryAccordionValues) !== 100) {
                    validationError = t(
                      "common:validation.packMixMustTotalOneHundred",
                    );
                  } else {
                    validationError = undefined;
                    setValidationErrors((_prev) => {
                      const newValidationErrors = deepClone(_prev);
                      Object.keys(newValidationErrors).forEach(
                        (key: string) => {
                          if (
                            key.includes(`category-${row.original.category}`)
                          ) {
                            newValidationErrors[key] = undefined;
                          }
                        },
                      );

                      return newValidationErrors;
                    });
                  }
                  break;
                case MacroAdjustmentCombinations.SalesMix_DLZShare:
                  if (value > 100 || value < 0) {
                    validationError = t(
                      "common:validation.greaterThanOrEqualZeroAndLessThanOrEqualHundred",
                    );
                  } else {
                    validationError = undefined;
                  }
                  break;
                default:
                  break;
              }
            } else {
              if (values.selectedAction === "percentage") {
                if (value < -100) {
                  validationError = t(
                    "common:validation.greaterThanOrEqualToMinusOneHundred",
                  );
                }
              } else if (
                [
                  MacroAdjustmentCombinations.Packaging_RecycledContent,
                  MacroAdjustmentCombinations.Packaging_RecoveryRate,
                ].includes(
                  adjustmentCombination as MacroAdjustmentCombinations,
                ) &&
                (value < 0 || value > 100)
              ) {
                validationError = t(
                  "common:validation.greaterThanOrEqualZeroAndLessThanOrEqualHundred",
                );
              } else if (
                [MacroAdjustmentCombinations.Packaging_NetLossRate].includes(
                  adjustmentCombination as MacroAdjustmentCombinations,
                ) &&
                ((value <= 0 && event.currentTarget.value) || value > 100)
              ) {
                validationError = t(
                  "common:validation.greaterThanZeroAndLessThanOrEqualHundred",
                );
              } else if (value < 0) {
                validationError = t("common:validation.greaterThanOrEqualZero");
              } else {
                validationError = undefined;
              }
            }

            setValidationErrors((prev: Record<number, string | undefined>) => ({
              ...prev,
              [`${adjustmentCombination === MacroAdjustmentCombinations.SalesMix_SalesGrowth ? row.original.country : adjustmentCombination === MacroAdjustmentCombinations.SalesMix_CategoryMix ? row.original.category : [MacroAdjustmentCombinations.SalesMix_PackMix, MacroAdjustmentCombinations.SalesMix_DLZShare].includes(adjustmentCombination as MacroAdjustmentCombinations) ? "packType-" + row.original.pack_type + "category-" + row.original.category : row.original.id}${
                [
                  MacroAdjustmentCombinations.Packaging_RecoveryRate,
                  MacroAdjustmentCombinations.Manufacturing_ManufacturingEur,
                  MacroAdjustmentCombinations.Logistics_DistanceTravelled,
                ].includes(adjustmentCombination as MacroAdjustmentCombinations)
                  ? row.original.country
                  : ""
              }`]: validationError,
            }));
          },
          onInput: (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            const decimalPlaces = getDecimalPlacesForDataStructureKey(
              adjustmentCombination as string,
            );
            const regex = getRegex(decimalPlaces);
            if (decimalPlaces === 0 && value.includes(".")) {
              event.target.value = value.replace(".", "");
            } else if (!regex.test(value)) {
              event.target.value = value.slice(0, -1);
            }
          },
          onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
            const paste = event.clipboardData.getData("text");
            const decimalPlaces = getDecimalPlacesForDataStructureKey(
              adjustmentCombination as string,
            );
            const regex = getRegex(decimalPlaces);
            if (!regex.test(paste)) {
              event.preventDefault();
            }
          },
          onFocus: () => {
            setEnableKeyboardShortcuts(false);
          },
        }),
        muiTableHeadCellProps: () => ({
          sx: {
            color: palette.primary.main,
            display: "flex",
            justifyContent: "center",
            width: "100%",
          },
        }),
      },
    ],
    [
      entityColumnHeader,
      baseYearColumnHeader,
      targetYearColumnHeader,
      t,
      adjustedValueColumnHeader,
      values.selectedEntities,
      values.payload.entities,
      values.selectedAction,
      adjustmentCombination,
      countries,
      categories,
      packTypes,
      isAdjustmentAllowed,
      validationErrors,
      handleOnChange,
      setValidationErrors,
      palette.primary.main,
    ],
  );

  useEffect(() => {
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
        setColumnVisibility({
          baseYear: false,
          targetYear: false,
          salesGrowth: false,
          country: false,
          category: false,
          pack_type: false,
          id: true,
        });
        break;
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_RecycledContent:
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
      case MacroAdjustmentCombinations.Packaging_NetLossRate:
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        setColumnVisibility({
          baseYear: true,
          targetYear: true,
          salesGrowth: false,
          country: false,
          category: false,
          pack_type: false,
          id: true,
        });
        break;
      case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
        setColumnVisibility({
          baseYear: false,
          targetYear: false,
          salesGrowth: true,
          country: true,
          category: false,
          pack_type: false,
          id: false,
        });
        break;
      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
        setColumnVisibility({
          baseYear: true,
          targetYear: true,
          salesGrowth: false,
          country: false,
          id: false,
          category: true,
          pack_type: false,
        });
        break;
      case MacroAdjustmentCombinations.SalesMix_PackMix:
      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        setColumnVisibility({
          baseYear: true,
          targetYear: true,
          salesGrowth: false,
          country: false,
          id: false,
          category: false,
          pack_type: true,
        });
        break;
      default:
        break;
    }
  }, [adjustmentCombination]);

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    editDisplayMode: "table",
    enableEditing: true,
    enableColumnActions: false,
    enableSorting: false,
    enablePagination: true,
    enableKeyboardShortcuts: enableKeyboardShortcuts,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
      columnOrder: [
        "id",
        "category",
        "pack_type",
        "baseYear",
        "targetYear",
        "country",
        "salesGrowth",
        "adjusted_value",
      ],
    },
    state: { columnVisibility },
    onColumnVisibilityChange:
      setColumnVisibility as OnChangeFn<VisibilityState>,
    getRowId: (row: any) => row.id,
    muiTableContainerProps: {
      sx: {
        border: "1px solid #E0E0E0",
        overflowY: "auto",
      },
    },
  });

  return <MaterialTable className="macro-accordion" table={table} />;
}

export default MacroAdjustmentStepThreeBasicTable;
